import { useState } from 'react';
import './Page.css';

export default function ACTPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faq = [
    {
      q: 'Is the ACT question bank available yet?',
      a: 'The ACT question bank is coming soon. In the meantime, our expert ACT tutors are available now for live 1:1 sessions tailored to your target score and weak areas.',
    },
    {
      q: 'Is 1:1 tutoring available for ACT?',
      a: 'Yes. Our tutors work with students on ACT English, Math, Reading, and Science. Sessions are tailored to your diagnostic results and target score. Book a free consultation to get started.',
    },
    {
      q: 'What subjects does ACT tutoring cover?',
      a: 'Our ACT tutors cover all four ACT sections: English, Math, Reading, and Science. Each session is built around your specific weak areas so you improve as efficiently as possible.',
    },
    {
      q: 'How quickly can I improve my ACT score?',
      a: 'Students who work consistently with a tutor — even a few sessions per week — see measurable accuracy gains within 2–3 weeks. Meaningful composite score improvement typically shows up within 6–8 weeks of focused prep.',
    },
    {
      q: 'Can I do SAT and ACT prep on the same account?',
      a: 'Yes. Your Scholar Sphere account gives you access to all content in one place. The SAT question bank is available now. ACT question bank coming soon.',
    },
  ];

  return (
    <div className="page-wrap">

      {/* ── Hero ── */}
      <div className="exam-hero">
        <div className="exam-hero-inner">
          <div className="exam-hero-text">
            <div className="exam-hero-badge">ACT Prep</div>
            <h1>Expert ACT tutoring.<br />Question bank coming soon.</h1>
            <p className="exam-hero-sub">
              Our ACT question bank is on the way. While you wait, work with a live expert tutor
              who will build a personalized strategy around your weak areas and target score.
              Real sessions. Real results.
            </p>
            <p className="exam-hero-proof">★★★★★ &nbsp; English · Math · Reading · Science — all covered</p>
            <div className="exam-hero-btns">
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
              <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
            </div>
          </div>
          <div className="exam-hero-visual" style={{ alignItems: 'center', paddingBottom: 32, display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: '28px 32px', maxWidth: 380, width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#f59e0b', color: '#78350f', fontSize: 11, fontWeight: 800, padding: '4px 12px', borderRadius: 20 }}>COMING SOON</div>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>ACT Question Bank</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                We're building a full ACT question bank with English, Math, Reading, and Science — every topic, every subtopic.
              </p>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 0 }}>
                  Expert ACT tutors are available <strong style={{ color: 'rgba(255,255,255,0.85)' }}>right now</strong> for live 1:1 sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">4</div>
              <div className="exam-stat-label">Subjects Covered in Tutoring</div>
            </div>
            <div>
              <div className="exam-stat-num">$55/hr</div>
              <div className="exam-stat-label">Live Expert Tutoring</div>
            </div>
            <div>
              <div className="exam-stat-num">97%</div>
              <div className="exam-stat-label">Students Improve</div>
            </div>
            <div>
              <div className="exam-stat-num">Coming Soon</div>
              <div className="exam-stat-label">ACT Question Bank</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* ── What tutoring covers ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <span className="section-label">Live 1:1 Tutoring</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Every ACT subject. Covered now.</h2>
            <p className="section-sub">Our tutors cover all four ACT sections with sessions tailored to your weaknesses.</p>
          </div>
          <div className="topic-grid">
            <div className="topic-card">
              <div className="topic-icon">📝</div>
              <div>
                <div className="topic-card-name">English</div>
                <div className="topic-card-subtopics">Production of Writing · Knowledge of Language · Conventions of Standard English</div>
              </div>
            </div>
            <div className="topic-card">
              <div className="topic-icon">📐</div>
              <div>
                <div className="topic-card-name">Math</div>
                <div className="topic-card-subtopics">Number & Quantity · Algebra · Functions · Geometry · Statistics & Probability</div>
              </div>
            </div>
            <div className="topic-card">
              <div className="topic-icon">📖</div>
              <div>
                <div className="topic-card-name">Reading</div>
                <div className="topic-card-subtopics">Key Ideas & Details · Craft & Structure · Integration of Knowledge & Ideas</div>
              </div>
            </div>
            <div className="topic-card">
              <div className="topic-icon">🔬</div>
              <div>
                <div className="topic-card-name">Science</div>
                <div className="topic-card-subtopics">Data Representation · Research Summaries · Conflicting Viewpoints</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tutoring feature ── */}
        <div className="feature-row">
          <div className="feature-row-text">
            <div className="feature-row-eyebrow">1:1 Expert Tutoring</div>
            <h2>A real expert, focused on your ACT score.</h2>
            <p>
              Our ACT tutors don't follow a generic curriculum. Every session is built around your
              diagnostic results and the specific areas where you're losing points. Live on Zoom,
              flexible scheduling, no long-term commitment required.
            </p>
            <ul className="check-list">
              <li>Tutor with a top ACT score</li>
              <li>Sessions built around your weak areas</li>
              <li>Custom strategy &amp; homework between sessions</li>
              <li>Flexible weekday &amp; weekend scheduling</li>
              <li>Combine with SAT question bank for self-paced practice</li>
            </ul>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
          </div>
          <div className="feature-row-visual" style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center', background: 'transparent', border: 'none', padding: 0 }}>
            <div style={{ background: 'var(--navy)', borderRadius: 16, padding: 28, color: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>While the question bank is in development…</div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  'Work 1:1 with an expert ACT tutor',
                  'Get a personalized study plan',
                  'Target your exact weak spots',
                  'Flexible Zoom sessions on your schedule',
                  'SAT question bank available right now',
                ].map(f => (
                  <li key={f} style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', paddingLeft: 20, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span>{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Testimonial ── */}
        <div className="page-section">
          <div className="mini-test-grid">
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
              <p className="mini-test-quote">"After just a few tailored sessions, my daughter's ACT score went up by 4 points. But more than that, she finally felt confident. She walked out of her final test smiling."</p>
              <div className="mini-test-name">Melissa D. — Parent</div>
            </div>
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
              <p className="mini-test-quote">"My tutor knew exactly where I was losing points on the Science section. Two sessions later and my score jumped by 3 points."</p>
              <div className="mini-test-name">Priya R. — Grade 11</div>
            </div>
          </div>
        </div>

        {/* ── Pricing ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">Get Started</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Start improving your ACT score now.</h2>
            <p className="section-sub">Expert tutoring available today. Question bank coming soon.</p>
          </div>
          <div className="two-col-section" style={{ marginBottom: 0 }}>
            <div className="path-card featured">
              <div className="path-icon">👨‍🏫</div>
              <div className="path-name">1:1 Expert Tutoring</div>
              <div className="path-tagline">Personalized, live on Zoom — available now</div>
              <div className="path-price">$55<span style={{ fontSize: 16, fontWeight: 400 }}>/hour</span></div>
              <div className="path-price-note">10 &amp; 20-hour bundles available</div>
              <ul className="path-features">
                <li>Tutor with top ACT score</li>
                <li>Sessions built around your weak areas</li>
                <li>Custom strategy &amp; homework</li>
                <li>Flexible weekday &amp; weekend scheduling</li>
                <li>Available for all 4 ACT sections</li>
              </ul>
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold" style={{ textAlign: 'center' }}>Book a Free Consultation</a>
            </div>
            <div className="path-card" style={{ opacity: 0.75 }}>
              <div className="path-icon">📚</div>
              <div className="path-name">ACT Question Bank</div>
              <div className="path-tagline" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: '#f59e0b', color: '#78350f', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 20 }}>COMING SOON</span>
              </div>
              <div className="path-price" style={{ color: 'var(--text-gray)' }}>—</div>
              <div className="path-price-note">Launching soon · Free tier planned</div>
              <ul className="path-features">
                <li>All 4 ACT subjects: English, Math, Reading, Science</li>
                <li>Questions organized by topic &amp; subtopic</li>
                <li>Full review mode after every session</li>
                <li>Aira AI chatbot explains every answer</li>
                <li>Topic-by-topic accuracy tracking</li>
              </ul>
              <a href="/signup" className="btn-navy" style={{ textAlign: 'center' }}>Create Free Account</a>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 24 }}>
            <h2 className="section-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faq.map((item, i) => (
              <details
                key={i}
                className="faq-item"
                open={openFaq === i}
                onClick={(e) => { e.preventDefault(); setOpenFaq(openFaq === i ? null : i); }}
              >
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="cta-strip" style={{ marginBottom: 32 }}>
          <h3>Get expert ACT tutoring today.</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 24 }}>
            Question bank coming soon. Live 1:1 tutoring available right now.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
            <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
          </div>
        </div>

      </div>
    </div>
  );
}
